// inside imports
import React, { useEffect, useState, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CartContext } from "./CartContext";
import LoadingSpinner from "./components/LoadingSpinner";
import ProductCard from "./ProductCard";
import Slider from "react-slick";
import { getOrFetch } from "./utils/apiCache";

function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [variations, setVariations] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [variationChoices, setVariationChoices] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { addToCart } = useContext(CartContext);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        // Product details (cached)
        const productData = await getOrFetch(
          `product:${id}`,
          () =>
            axios
              .get(`https://floriakw.com/wp-json/wc/v3/products/${id}`,
                {
                  auth: {
                    username: "ck_39cd5450af63b97a35e502d9a0a75925866fefab",
                    password: "cs_15701bd9b4392a3b6e5a3d5fa4d326d7acb0a0bf",
                  },
                }
              )
              .then((r) => r.data),
          { ttlMs: 10 * 60 * 1000 }
        );
        setProduct(productData);

        // Variations for variable products (cached)
        if (productData.type === "variable") {
          const variationData = await getOrFetch(
            `variations:${id}`,
            () =>
              axios
                .get(`https://floriakw.com/wp-json/wc/v3/products/${id}/variations`, {
                  auth: {
                    username: "ck_39cd5450af63b97a35e502d9a0a75925866fefab",
                    password: "cs_15701bd9b4392a3b6e5a3d5fa4d326d7acb0a0bf",
                  },
                  params: { per_page: 100 },
                })
                .then((r) => r.data),
            { ttlMs: 10 * 60 * 1000 }
          );
          setVariations(variationData);
        } else {
          setVariations([]);
        }

        // Related products (cached)
        if (productData.related_ids?.length > 0) {
          const relatedKey = `related:${[...productData.related_ids].sort((a,b)=>a-b).join(',')}`;
          const relatedData = await getOrFetch(
            relatedKey,
            () =>
              axios
                .get(`https://floriakw.com/wp-json/wc/v3/products`, {
                  auth: {
                    username: "ck_39cd5450af63b97a35e502d9a0a75925866fefab",
                    password: "cs_15701bd9b4392a3b6e5a3d5fa4d326d7acb0a0bf",
                  },
                  params: { include: productData.related_ids.join(",") },
                })
                .then((r) => r.data),
            { ttlMs: 10 * 60 * 1000 }
          );
          setRelated(relatedData);
        } else {
          setRelated([]);
        }
      } catch (err) {
        console.error("Error loading product:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // check if variation matches selected attributes
  useEffect(() => {
    if (product?.type === "variable" && variations.length > 0) {
      const found = variations.find((v) =>
        v.attributes.every(
          (attr) => variationChoices[attr.name] === attr.option
        )
      );
      setSelectedVariation(found || null);
    }
  }, [variationChoices, variations, product]);

  const handleAttributeChange = (name, value) => {
    setVariationChoices((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddToCart = () => {
    if (product.type === "variable" && !selectedVariation) {
      alert("يرجى اختيار جميع الخصائص قبل الإضافة إلى السلة");
      return;
    }
    const itemToAdd =
      product.type === "variable"
        ? { ...product, id: selectedVariation.id, qty: quantity, variation: selectedVariation }
        : { ...product, qty: quantity };

    addToCart(itemToAdd);
  };

  if (isLoading) {
    return (
      <div className="container" style={{ minHeight: "50vh" }}>
        <LoadingSpinner size="medium" color="black" withText={true} />
      </div>
    );
  }

  if (!product) return <p className="container">المنتج غير موجود</p>;

  return (
    <div className="container">
      <div className="product-detail">
        {/* Right: gallery */}
        <div className="product-gallery">
          {product.images?.length > 0 && (
            <Slider dots arrows infinite slidesToShow={1} slidesToScroll={1}>
              {product.images.map((img) => (
                <div key={img.id}>
                  <img src={img.src} alt={product.name} />
                </div>
              ))}
            </Slider>
          )}
        </div>

        {/* Left: info */}
        <div className="product-info">
          {/* Breadcrumb */}
          <nav className="breadcrumbs">
            <Link to="/">الرئيسية</Link>
            {product.categories?.map((cat, i) => (
              <span key={cat.id}>
                {" / "}
                <Link to={`/category/${cat.id}`}>{cat.name}</Link>
              </span>
            ))}
            {" / "}
            <span>{product.name}</span>
          </nav>

          <h2>{product.name}</h2>

          {/* Price */}
          <div className="price">
            {selectedVariation ? (
              <>
                {selectedVariation.sale_price ? (
                  <>
                    <span className="regular-price">
                      {parseFloat(selectedVariation.regular_price).toFixed(2)} KWD
                    </span>
                    <span className="sale-price">
                      {parseFloat(selectedVariation.sale_price).toFixed(2)} KWD
                    </span>
                  </>
                ) : (
                  <span className="sale-price">
                    {parseFloat(selectedVariation.price).toFixed(2)} KWD
                  </span>
                )}
              </>
            ) : (
              <span>اختر الخصائص لعرض السعر</span>
            )}
          </div>

          <div
            className="short-desc"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />

          {/* Variations */}
          {product.type === "variable" &&
            product.attributes.map((attr) => (
              <div key={attr.id} className="variation-box-group">
                <label>{attr.name}:</label>
                <div className="variation-boxes">
                  {attr.options.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={`variation-box ${
                        variationChoices[attr.name] === opt ? "active" : ""
                      }`}
                      onClick={() => handleAttributeChange(attr.name, opt)}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}


          {/* Quantity + Add */}
          <div className="addtocart">
            <div className="quantity-control">
              <button onClick={() => setQuantity(quantity > 1 ? quantity - 1 : 1)}>-</button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)}>+</button>
            </div>

            <button className="add-to-cart-btn" onClick={handleAddToCart}>
              إضافة إلى السلة
            </button>
          </div>
        </div>
      </div>

      {/* Description */}
      {product.description && (
        <div className="product-description">
          <h3>الوصف</h3>
          <div dangerouslySetInnerHTML={{ __html: product.description }} />
        </div>
      )}

      {/* Related */}
      {related.length > 0 && (
        <div className="related-products">
          <h2>منتجات ذات صلة</h2>
          <div className="grid">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductPage;
